/**
 * Ekranda atılan imzanın (MASAK kimlik tespiti / nakit işlem beyanı) çizim verisi.
 *
 * İmza kalem izlerinden oluşur; her iz çizim alanı koordinatlarında [x, y] noktalarıdır. Sunucuya
 * SVG değil bu noktalar gider — SVG'yi sunucu üretir (JewApi MasakSignatureSvg). Kurallar o sınıfla
 * (ve mobildeki signature.helpers ile) aynı tutulur: en az 5, en çok 6000 nokta / 200 iz.
 */

export type SignaturePoint = [number, number];

export interface SignatureValue {
  strokes: SignaturePoint[][];
  /** Çizim alanının CSS piksel boyutu (noktaların koordinat sistemi). */
  width: number;
  height: number;
}

export const SIGNATURE_MIN_POINTS = 5;
export const SIGNATURE_MAX_POINTS = 6000;
export const SIGNATURE_MAX_STROKES = 200;

const pointCount = (strokes: SignaturePoint[][]) => strokes.reduce((sum, s) => sum + s.length, 0);

/** İmza sayılmayacak kadar kısa (yanlışlıkla tıklama) ya da boş. */
export function isSignatureEmpty(value: SignatureValue | null | undefined): boolean {
  return !value || pointCount(value.strokes) < SIGNATURE_MIN_POINTS;
}

/** Sunucunun kabul etmeyeceği kadar karmaşık. */
export function isSignatureTooComplex(value: SignatureValue): boolean {
  return value.strokes.length > SIGNATURE_MAX_STROKES || pointCount(value.strokes) > SIGNATURE_MAX_POINTS;
}

/** Çok yakın noktalar (el titremesi) atlanır; nokta sayısı gereksiz büyümez. */
export function shouldAddPoint(last: SignaturePoint | undefined, next: SignaturePoint, minDistance = 1.5): boolean {
  if (!last) return true;
  return Math.hypot(next[0] - last[0], next[1] - last[1]) >= minDistance;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Sunucuya gidecek istek: noktalar 1 haneye yuvarlanır. */
export function toSignatureRequest(value: SignatureValue) {
  return {
    strokes: value.strokes.filter((s) => s.length > 0).map((s) => s.map(([x, y]) => [round1(x), round1(y)])),
    width: round1(value.width),
    height: round1(value.height),
  };
}

/**
 * Kaydetmeden önceki denetim: çizim var ama imza sayılmıyorsa mesaj, sorun yoksa null.
 * Çizim yoksa (null) imza isteğe bağlı olduğundan hata değildir.
 */
export function signatureError(value: SignatureValue | null | undefined): string | null {
  if (!value) return null;
  if (isSignatureEmpty(value)) return 'İmza çok kısa; tam imza atın ya da temizleyin.';
  if (isSignatureTooComplex(value)) return 'İmza çok karmaşık; temizleyip yeniden atın.';
  return null;
}
